import './styles.css';
import { useState, useMemo } from 'react';
import MicLargeIcon from '@/assets/icons/mic-large.svg';
import MicReg from '@/assets/icons/microphone.svg';
import { Button } from '@/shared/ui';
import ActiveVoiceRecognition from './active-recognition';
import { ErrorMessage } from '../../helpers/speech-recognition';
import useDeviceType from '@/shared/hooks/useDeviceType';


interface IProps {
    onClose: (transcript: string) => void;
    onToKeyboard: () => void;
    subject?: string;
}

export const VoiceRecognitionPopup = ({ onClose, onToKeyboard, subject }: IProps) => {
    const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null);

    const onDidnGetThat = () => {
        setErrorMessage('no-speech');
    }

    const handleTryAgain = () => {
        setErrorMessage(null);
    }

    const errorEncountered = (event: { error: string }) => {
        setErrorMessage(event?.error as ErrorMessage);
    }

    const uiRenderer = () => {
        if (errorMessage) {
            if (['no-speech', 'aborted', 'audio-capture', 'network'].includes(errorMessage)) {
                return (
                    <div className='flex flex-col items-center font-agrandir'>
                        <h3 className="pb-3 text-center font-agrandir text-26 sm:text-3xl">Didn't hear that. Try again.</h3>
                        <button data-testid="button-voice-recognition-try-again" onClick={handleTryAgain} className="relative">
                            <img width={148} className="h-[148px] w-[148px]" src={MicLargeIcon} alt="mic-icon" />
                        </button>
                        <span className='font-bold text-19 sm:text-22'>Tap microphone to try again</span>
                    </div>
                )
            }

            let title = '';
            let canSuggestConfigChange = false;
            if (['not-allowed', 'service-not-allowed'].includes(errorMessage)) {
                title = "Microphone is currently disabled";
                canSuggestConfigChange = true;
            } else {
                title = "Voice search not supported";
            }

            return (
                <div>
                    <MicrophoneError canSuggestConfigChange={canSuggestConfigChange} title={title} onSearchByTypingClick={onToKeyboard} subject={subject} />
                </div>
            )
        }

        return <ActiveVoiceRecognition errorEncountered={errorEncountered} onClose={onClose} onDidnGetThat={onDidnGetThat} />
    }


    return (
        <div className="flex flex-col items-center gap-10 pt-6">
            {uiRenderer()}
        </div>
    );
};


const MicrophoneError = ({ title, onSearchByTypingClick, canSuggestConfigChange, subject }: { title: string; onSearchByTypingClick?: () => void; canSuggestConfigChange: boolean, subject?: string }) => {
    const { deviceType } = useDeviceType();
    const isDesktop = deviceType === 'desktop';
    const conditionalAlternativeText = useMemo(() => {
        if (subject) {
            return `Alternatively, you can search by typing in your ${subject}.`
        }

        return 'Alternatively, you can search by typing.'
    }, [subject])

    const conditionalSuggestionText = useMemo(() => {
        const noun = subject || 'items'
        return isDesktop ? `To add ${noun} faster, open your browser settings and allow microphone permissions.` : `To add ${noun} faster, you need to grant microphone and voice recognition permissions in your device settings`
    }, [isDesktop, subject])

    return (
        <div className="flex flex-col items-center" data-testid="voice-recognition-container">
            <div className="flex flex-col bg-primary-700 h-[45px] w-[45px] sm:h-[55px] sm:w-[55px] items-center justify-center rounded-full">
                <img
                    src={MicReg}
                    alt="mic-icon"
                    className="w-[25px] h-[25px] sm:w-[35px] sm:h-[35px]"
                />
            </div>
            <div className="flex flex-col items-center pb-5 pt-4 gap-3">
                <span className="text-center font-agrandir text-22 sm:text-25 leading-tight" data-testid="voice-recognition-title"> {title}</span>
                <div className="flex flex-col gap-4 font-dmSans text-base sm:gap-5 sm:text-xl">
                    {canSuggestConfigChange && <span className="text-center" data-testid="voice-recognition-suggestion-text">
                        {conditionalSuggestionText}
                    </span>}
                    <span className="text-center text-15 sm:text-19 font-dmSansMedium font-extrabold leading-tight" data-testid="voice-recognition-alternative-text">{conditionalAlternativeText}</span>
                </div>
            </div>
            <Button testId="button-search-by-typing" showEndIcon className="w-full" onClick={onSearchByTypingClick}>
                Search by Typing
            </Button>
        </div >
    );
};